import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { AnimatedTextField, Box, GradientIcon, IconButton, Typography } from 'seagull';
import { ReturnArrow } from 'seagull/icons/arrows';
import { Search } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useDraftsAiSearchStore } from '../hooks/useDraftsAiSearchStore';
import styles from './DraftsAiSearchInput.module.scss';
import { useDraftsAiSearchSubmit } from './hooks/useDraftsAiSearchSubmit';
export const GetDraftsAiSearchInputTID = (parentTestId)=>getTestIds(parentTestId);
export const DraftsAiSearchInput = (param)=>{
    let { testId, placeholder, initialQuery, onQueryChange } = param;
    const { DraftsAiSearchInputTestIds } = useMemo(()=>GetDraftsAiSearchInputTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const { lastQuery, filterEditedSinceLastSearch } = useDraftsAiSearchStore(useShallow((s)=>({
            lastQuery: s.lastQuery,
            filterEditedSinceLastSearch: s.filterEditedSinceLastSearch
        })));
    const { submit, isSubmitting } = useDraftsAiSearchSubmit();
    const [value, setValue] = useState(lastQuery);
    const [syncedLastQuery, setSyncedLastQuery] = useState(lastQuery);
    if (lastQuery !== syncedLastQuery) {
        setSyncedLastQuery(lastQuery);
        setValue(lastQuery);
    }
    useEffect(()=>{
        const initial = (initialQuery !== null && initialQuery !== void 0 ? initialQuery : '').trim();
        if (initial === '' || useDraftsAiSearchStore.getState().filter != null) return;
        setValue(initial);
        submit(initial).catch(()=>undefined);
    // WHY: mount-only restore of the initial query — must not re-run when
    // `initialQuery` / `submit` change, so a later prop update can't re-trigger it.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, []);
    const exampleQueries = useMemo(()=>[
            t.exampleQueryModifiedBy(),
            t.exampleQueryEffectiveDate(),
            t.exampleQueryExpiringDiscount(),
            t.exampleQueryPriceIncrease()
        ], []);
    const trimmedValue = value.trim();
    const showSubmitCue = trimmedValue !== '' && trimmedValue !== lastQuery;
    const showFiltersEditedBadge = filterEditedSinceLastSearch && trimmedValue === lastQuery;
    const isFilled = showSubmitCue && !isSubmitting;
    const isQueryActive = trimmedValue !== '' && !showFiltersEditedBadge;
    const handleChange = (event)=>{
        setValue(event.target.value);
    };
    const handleSubmit = ()=>{
        if (trimmedValue === '') return;
        onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange(trimmedValue);
        submit(trimmedValue).catch(()=>undefined);
    };
    const handleKeyDown = (event)=>{
        if (event.key !== 'Enter') return;
        event.preventDefault();
        handleSubmit();
    };
    return /*#__PURE__*/ _jsx(Box, {
        testId: DraftsAiSearchInputTestIds.Root(),
        className: styles.root,
        children: /*#__PURE__*/ _jsxs(Box, {
            className: cn(styles.pill, {
                [styles.filled]: isFilled,
                [styles.processing]: isSubmitting
            }),
            children: [
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.searchIcon,
                    children: /*#__PURE__*/ _jsx(GradientIcon, {
                        icon: /*#__PURE__*/ _jsx(Search, {
                            size: 20
                        }),
                        size: 20,
                        animated: isQueryActive
                    })
                }),
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.field,
                    children: /*#__PURE__*/ _jsx(AnimatedTextField, {
                        testId: DraftsAiSearchInputTestIds.TextField(),
                        bi: {
                            name: 'drafts_ai_search_query'
                        },
                        suggestions: exampleQueries,
                        value: value,
                        onChange: handleChange,
                        placeholder: placeholder,
                        className: styles.animatedField,
                        inputRootClass: styles.bareInput,
                        inputProps: {
                            onKeyDown: handleKeyDown,
                            className: cn(styles.inputElement, {
                                [styles.inputElementMuted]: showFiltersEditedBadge
                            })
                        }
                    })
                }),
                showSubmitCue && /*#__PURE__*/ _jsx(Box, {
                    testId: DraftsAiSearchInputTestIds.SubmitCue(),
                    className: styles.submitCue,
                    children: /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'drafts_ai_search_submit_cue'
                        },
                        onClick: handleSubmit,
                        size: "small",
                        noPadding: true,
                        skin: "primaryTransparent",
                        "aria-label": t.submitSearch(),
                        children: /*#__PURE__*/ _jsx(ReturnArrow, {
                            size: 20
                        })
                    })
                }),
                showFiltersEditedBadge && /*#__PURE__*/ _jsx(Box, {
                    testId: DraftsAiSearchInputTestIds.FiltersEditedBadge(),
                    className: styles.filtersEditedBadge,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        children: t.filtersEdited()
                    })
                })
            ]
        })
    });
};
